// src/components/ExcelUploadWizard/ExcelUploadContactWizard.js

import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  FileSpreadsheet,
  Upload,
  Trash2,
  Plus,
  ArrowLeft,
  ArrowRight,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileUp,
  Edit3,
  RefreshCw,
  AlertTriangle,
  Save,
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";

export default function ExcelUploadContactWizard({
  userSchema,
  setUserSchema,
  onClose,
  onSuccess
}) {
  const { auth } = useContext(AuthContext);

  // Steps: 1=Select&Analyze, 2=ConfigureSchema (only if GPT), 3=Review&Confirm
  const [step, setStep] = useState(1);
  const [excelFile, setExcelFile] = useState(null);

  // If the server gleaned schema from DB => schemaSource='db'
  // otherwise => 'gpt'
  const [schemaSource, setSchemaSource] = useState("gpt");
  const [schemaMapping, setSchemaMapping] = useState(userSchema || []);
  const [sampleMappings, setSampleMappings] = useState([]);
  const [reviewRows, setReviewRows] = useState([]); // each: { rowIndex, mappedRow, conflictType, isCompanyNew, ... }

  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("info");

  // If you want a default contactType at the top:
  const [selectedContactType, setSelectedContactType] = useState("Individual");

  const mandatoryFields = ["Name", "Email", "Number"];

  function isMandatoryField(col) {
    return mandatoryFields.some((f) => f.toLowerCase() === col.toLowerCase());
  }

  function handleFileChange(e) {
    setExcelFile(e.target.files[0] || null);
    setStatusMsg("");
    setStatusType("info");
  }

  function renderStatusMessage() {
    if (!statusMsg) return null;
    let icon, bgColor, borderColor;
    switch (statusType) {
      case "success":
        icon = <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />;
        bgColor = "bg-green-900/20";
        borderColor = "border-green-800";
        break;
      case "error":
        icon = <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />;
        bgColor = "bg-red-900/20";
        borderColor = "border-red-800";
        break;
      case "warning":
        icon = <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />;
        bgColor = "bg-yellow-900/20";
        borderColor = "border-yellow-800";
        break;
      default:
        icon = loading
          ? <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />
          : <FileSpreadsheet className="h-5 w-5 text-blue-500 flex-shrink-0" />;
        bgColor = "bg-blue-900/20";
        borderColor = "border-blue-800";
    }
    return (
      <div className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${bgColor} border ${borderColor}`}>
        {icon}
        <span>{statusMsg}</span>
      </div>
    );
  }

  // ---------------- STEP 1: Analyze ----------------
  async function handleAnalyze() {
    if (!excelFile) {
      setStatusMsg("No file selected.");
      setStatusType("warning");
      return;
    }
    setLoading(true);
    setStatusMsg("Analyzing Excel data...");
    setStatusType("info");

    try {
      const formData = new FormData();
      formData.append("excelFile", excelFile);
      formData.append("contactType", selectedContactType);

      // Post to /contacts-excel/analyze-excel
      const resp = await axios.post("/api/myteconstruction/contacts-excel/analyze-excel", formData, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      const recommended = resp.data.recommendedSchema || [];
      const sample = resp.data.sampleMappings || [];
      const fromDB = resp.data.schemaSource === "db";

      setSchemaMapping(recommended);
      setSampleMappings(sample);
      setSchemaSource(fromDB ? "db" : "gpt");

      if (fromDB) {
        // If gleaned from DB, skip step 2 => go directly to step 3
        setStatusMsg("Existing schema gleaned from DB. Skipping rename step.");
        setStatusType("success");
        setStep(3);
      } else {
        setStatusMsg("Analysis complete. You can rename/add columns if needed.");
        setStatusType("success");
        setStep(2);
      }
    } catch (err) {
      console.error("Analyze error:", err);
      if (err.response?.status === 400) {
        setStatusMsg(err.response.data.error || "Analyze step failed due to missing mandatory columns.");
      } else {
        setStatusMsg("Analyze step failed.");
      }
      setStatusType("error");
    } finally {
      setLoading(false);
    }
  }

  // ---------------- STEP 2: Configure Schema (only if GPT) ----------------
  function renameColumn(index, newVal) {
    const updated = [...schemaMapping];
    updated[index] = newVal;
    setSchemaMapping(updated);
  }

  function addColumn() {
    setSchemaMapping([...schemaMapping, ""]);
  }

  function removeColumn(idx) {
    const colName = schemaMapping[idx] || "";
    if (isMandatoryField(colName)) {
      setStatusMsg(`Cannot remove mandatory field: ${colName}`);
      setStatusType("warning");
      return;
    }
    const updated = [...schemaMapping];
    updated.splice(idx, 1);
    setSchemaMapping(updated);
  }

  function renderSampleTable() {
    if (!sampleMappings.length) {
      return (
        <div className="text-sm text-gray-400 flex items-center gap-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
          <AlertCircle className="h-4 w-4 text-gray-500" />
          <p>No sample mappings available.</p>
        </div>
      );
    }
    return (
      <div className="border border-gray-700 rounded-lg overflow-x-auto max-h-48 shadow-md">
        <table className="text-sm w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="p-2 border-b border-gray-700 text-gray-300">Row #</th>
              {schemaMapping.map((col, idx) => (
                <th key={idx} className="p-2 border-b border-gray-700 text-left text-gray-300">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sampleMappings.map((sample, i) => {
              const rowIndex = sample.rowIndex || i + 1;
              const data = sample.mapped || sample; // sometimes sample has 'mapped' property
              return (
                <tr
                  key={i}
                  className="odd:bg-gray-900 even:bg-gray-850 hover:bg-gray-800 transition-colors"
                >
                  <td className="p-2 border-b border-gray-800 text-center">{rowIndex}</td>
                  {schemaMapping.map((colName, colIdx) => {
                    const val = data[colName];
                    const displayVal =
                      typeof val === "object" ? JSON.stringify(val) : String(val || "");
                    return (
                      <td key={colIdx} className="p-2 border-b border-gray-800">
                        {displayVal}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  async function handleParse() {
    if (!excelFile) {
      setStatusMsg("No file selected.");
      setStatusType("warning");
      return;
    }
    setLoading(true);
    setStatusMsg("Parsing entire Excel with your chosen schema...");
    setStatusType("info");

    try {
      const formData = new FormData();
      formData.append("excelFile", excelFile);
      formData.append("finalSchema", JSON.stringify(schemaMapping));
      formData.append("contactType", selectedContactType);

      const parseResp = await axios.post("/api/myteconstruction/contacts-excel/parse-excel-data", formData, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      const { parsedRecords, message } = parseResp.data;
      if (!parsedRecords || !parsedRecords.length) {
        setStatusMsg("Parsing returned no data.");
        setStatusType("warning");
        setLoading(false);
        return;
      }

      setReviewRows(parsedRecords); // store row-level data
      setStatusMsg(message || `Parsed ${parsedRecords.length} rows. Review & confirm below.`);
      setStatusType("success");
      setStep(3);
    } catch (err) {
      console.error("Parse error:", err);
      setStatusMsg("Parse step failed.");
      setStatusType("error");
    } finally {
      setLoading(false);
    }
  }

  // ---------------- STEP 3: Review & Confirm  ----------------
  // conflictType => "email" or "duplicateEmail" => user picks skip or overwrite
  // else => default "insert" or skip
  function setRowAction(rowIndex, action) {
    setReviewRows(prev => prev.map(r => {
      if (r.rowIndex === rowIndex) {
        return { ...r, action };
      }
      return r;
    }));
  }

  async function handleConfirmSave() {
    setLoading(true);
    setStatusMsg("Saving data to database...");
    setStatusType("info");

    try {
      const newRows = [];
      const upsertRows = [];

      for (const r of reviewRows) {
        // default action if no conflict => insert
        // if conflict => skip or overwrite
        // but let them pick from the UI
        let action = r.action;
        // If user never explicitly set an action, fallback:
        if (!action) {
          if (r.conflictType === "email" || r.conflictType === "duplicateEmail") {
            action = "skip"; // default to skip
          } else {
            action = "insert"; // default if no conflict
          }
        }
        if (action === "insert") {
          newRows.push(r);
        } else if (action === "overwrite") {
          upsertRows.push({ newData: r });
        }
        // skip => do nothing
      }

      const resp = await axios.post(
        "/api/myteconstruction/contacts-excel/confirm-excel-data",
        { newRows, upsertRows },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );

      setStatusMsg(resp.data.message || "Contacts saved successfully.");
      setStatusType("success");

      // Optionally store the schema
      if (setUserSchema) {
        setUserSchema(schemaMapping);
      }
      // If there's an onSuccess callback
      if (onSuccess) {
        setTimeout(onSuccess, 2000);
      }
    } catch (err) {
      console.error("Confirm step failed:", err);
      setStatusMsg("Confirm step failed.");
      setStatusType("error");
    } finally {
      setLoading(false);
    }
  }

  function renderStepContent() {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 shadow-md">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <FileUp className="h-5 w-5 text-blue-500" />
                <span>Step 1: Select Excel File</span>
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <label className="flex-1 w-full">
                  <div className="relative cursor-pointer bg-gray-900 border border-gray-700 rounded-lg p-4 text-center hover:bg-gray-850 transition-colors">
                    <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                    <p className="text-sm text-gray-300 mb-1">
                      {excelFile ? excelFile.name : "Click to select Excel file"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {excelFile ? `${(excelFile.size / 1024).toFixed(1)} KB` : ".xlsx files only"}
                    </p>
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </label>
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !excelFile}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none shadow-md"
                >
                  {loading
                    ? (<><Loader2 className="h-4 w-4 animate-spin" /><span>Analyzing...</span></>)
                    : (<><RefreshCw className="h-4 w-4" /><span>Analyze Excel</span></>)
                  }
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        // If gleaned from DB => skip rename step
        if (schemaSource === "db") {
          return (
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 shadow-md">
              <AlertCircle className="h-6 w-6 text-yellow-500" />
              <p className="text-sm">
                Schema gleaned from DB. Skipping rename step...
              </p>
            </div>
          );
        }
        // Otherwise, user can rename columns
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 shadow-md">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <Edit3 className="h-5 w-5 text-blue-500" />
                <span>Step 2: Configure Schema</span>
              </h3>
              <div className="mb-4 space-y-2">
                <p className="text-sm font-medium text-gray-300">
                  Edit or reorder headers:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-1">
                  {schemaMapping.map((col, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        value={col}
                        onChange={(e) => renameColumn(idx, e.target.value)}
                        className={`bg-gray-900 px-3 py-2 rounded-md text-white flex-1 border ${
                          isMandatoryField(col)
                            ? "border-blue-700 bg-blue-900/20"
                            : "border-gray-700"
                        }`}
                        disabled={isMandatoryField(col)}
                        placeholder="Column name"
                      />
                      {!isMandatoryField(col) ? (
                        <button
                          onClick={() => removeColumn(idx)}
                          className="bg-red-600 hover:bg-red-700 p-2 rounded-md text-white transition-colors"
                          title="Remove column"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : (
                        <div className="bg-blue-900/30 text-xs text-blue-300 px-2 py-1 rounded border border-blue-800">
                          Required
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addColumn}
                  className="bg-green-600 hover:bg-green-700 px-3 py-2 rounded-md text-sm flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Column</span>
                </button>
              </div>
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-300 mb-2">
                  Sample Data Preview:
                </p>
                {renderSampleTable()}
              </div>
              <button
                onClick={handleParse}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none shadow-md"
              >
                {loading
                  ? (<><Loader2 className="h-4 w-4 animate-spin" /><span>Parsing...</span></>)
                  : (<><Upload className="h-4 w-4" /><span>Parse Full Excel</span></>)
                }
              </button>
            </div>
          </div>
        );

      case 3:
        // Step 3: Review & Confirm
        return (
          <div className="space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 shadow-md">
              <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <span>Step 3: Review & Confirm</span>
              </h3>
              {!reviewRows.length && (
                <p className="text-sm text-gray-400">
                  No rows to review. Please go back and parse the file again.
                </p>
              )}

              {reviewRows.length > 0 && (
                <div className="max-h-96 overflow-y-auto pr-1">
                  {reviewRows.map((r, i) => {
                    // conflictType: "email" or "duplicateEmail" => skip/overwrite
                    // otherwise => skip/insert
                    const conflict = r.conflictType;
                    let defaultAction = "insert";
                    if (conflict === "email" || conflict === "duplicateEmail") {
                      defaultAction = "skip";
                    }
                    const action = r.action || defaultAction;

                    return (
                      <div
                        key={i}
                        className={`border rounded-lg p-3 mb-3 text-sm ${
                          conflict
                            ? "border-yellow-600 bg-yellow-900/10"
                            : "border-gray-700 bg-gray-900"
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-200">
                              Row {r.rowIndex + 1}
                              {conflict === "email" && " (Email conflict)"}
                              {conflict === "duplicateEmail" && " (Duplicate email)"}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {conflict
                              ? (
                                <>
                                  <button
                                    onClick={() => setRowAction(r.rowIndex, "skip")}
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      action === "skip"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                    }`}
                                  >
                                    Skip
                                  </button>
                                  <button
                                    onClick={() => setRowAction(r.rowIndex, "overwrite")}
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      action === "overwrite"
                                        ? "bg-yellow-600 text-white"
                                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                    }`}
                                  >
                                    Overwrite
                                  </button>
                                </>
                              )
                              : (
                                <>
                                  <button
                                    onClick={() => setRowAction(r.rowIndex, "skip")}
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      action === "skip"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                    }`}
                                  >
                                    Skip
                                  </button>
                                  <button
                                    onClick={() => setRowAction(r.rowIndex, "insert")}
                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                      action === "insert"
                                        ? "bg-green-600 text-white"
                                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                    }`}
                                  >
                                    Insert
                                  </button>
                                </>
                              )
                            }
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {r.existingContactId && (
                            <div className="bg-gray-800 p-2 rounded border border-gray-700">
                              <p className="text-yellow-400 mb-1">Existing Contact ID:</p>
                              <p className="text-gray-300 text-xs">
                                {r.existingContactId}
                              </p>
                            </div>
                          )}
                          <div className="bg-gray-800 p-2 rounded border border-gray-700">
                            <p className="text-gray-400 mb-1">Parsed Data:</p>
                            {Object.entries(r.mappedRow).map(([key, value]) => {
                              const displayVal =
                                typeof value === "object" ? JSON.stringify(value) : String(value || "");
                              return (
                                <p key={key} className="text-gray-300 text-xs">
                                  {key}: <span className="text-gray-400">{displayVal}</span>
                                </p>
                              );
                            })}
                            {r.isCompanyNew && (
                              <p className="text-xs text-green-400 mt-1">
                                (New Company: “{r.mappedRow["Company Name"]}”)
                              </p>
                            )}
                            {r.isOfficeNew && (
                              <p className="text-xs text-green-400">
                                (New Office: “{r.mappedRow["Address"]}”)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {reviewRows.length > 0 && (
                <button
                  onClick={handleConfirmSave}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:pointer-events-none shadow-md mt-3"
                >
                  {loading
                    ? (<><Loader2 className="h-4 w-4 animate-spin" /><span>Processing...</span></>)
                    : (<><Save className="h-4 w-4" /><span>Confirm and Save</span></>)
                  }
                </button>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  // Render
  return (
    <div className="text-white">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-blue-500" />
          <span>Contacts Excel Upload</span>
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Example Default Contact Type selector */}
      <div className="mb-4">
        <label className="block text-sm text-gray-300 mb-1">Default Contact Type</label>
        <select
          value={selectedContactType}
          onChange={(e) => setSelectedContactType(e.target.value)}
          className="bg-gray-900 text-white p-2 rounded"
        >
          <option value="Individual">Individual</option>
          <option value="Client">Client</option>
          <option value="Supplier">Supplier</option>
          <option value="Subcontractor">Subcontractor</option>
        </select>
      </div>

      {/* Steps indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3].map((stepNum) => (
            <div key={stepNum} className={`flex items-center ${stepNum < 3 ? "flex-1" : ""}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                  stepNum === step
                    ? "bg-blue-600 text-white"
                    : stepNum < step
                    ? "bg-green-600 text-white"
                    : "bg-gray-700 text-gray-400"
                }`}
              >
                {stepNum < step ? <CheckCircle className="h-4 w-4" /> : stepNum}
              </div>
              {stepNum < 3 && (
                <div className={`h-1 flex-1 ${stepNum < step ? "bg-green-600" : "bg-gray-700"}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-400">
          <div className="w-16 text-center">Select File</div>
          <div className="w-16 text-center">Configure</div>
          <div className="w-16 text-center">Review</div>
        </div>
      </div>

      {renderStatusMessage()}
      {renderStepContent()}

      {/* Wizard nav (optional) */}
      <div className="mt-6 flex justify-between">
        <div>
          {step > 1 && step < 3 && (
            <button
              onClick={() => setStep(step - 1)}
              className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors"
              disabled={loading}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          {step < 2 && schemaSource === "gpt" && !loading && (
            <button
              onClick={() => setStep(step + 1)}
              className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors"
            >
              <span>Next</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

ExcelUploadContactWizard.propTypes = {
  userSchema: PropTypes.arrayOf(PropTypes.string).isRequired,
  setUserSchema: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
